import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';

type UseFlowMetricsSyncOptions = {
  /** When false, no fetch is issued (e.g. flow node not loaded yet). Defaults to true. */
  enabled?: boolean;
};

type UseFlowMetricsSyncReturn = {
  /** Stable-identity refresh for the toolbar button — see note below. */
  refresh: () => void;
};

/**
 * Drives per-node flow metrics for surfaces that render the DAG in PREVIEW mode.
 *
 * Background: `FlowNodes` rendered with `isPreview` disables its own metrics
 * fetch (to avoid a request storm across many preview canvases), so the PARENT
 * surface must fetch. Several parents re-implemented this inconsistently and got
 * it wrong — the toggle (which only dispatches `setFlowMetricsInterval` to Redux)
 * did nothing because no effect re-fetched on interval change. This hook is the
 * single correct implementation; use it instead of hand-rolling the effect.
 *
 * It does two things:
 *  1. Fetches on mount AND whenever `flowMetricsInterval` changes — so the toggle
 *     is honored (the effect depends on the interval).
 *  2. Returns a `refresh()` with a STABLE identity that reads the interval through
 *     a ref at call-time. `SourcePipeline`'s memo comparator intentionally drops
 *     the `refreshPipeline` prop, so a changing callback identity never reaches
 *     the toolbar refresh button — it stays bound to the first closure. Reading
 *     the ref means that frozen closure still uses the current selection. See
 *     NEX-21823.
 *
 * The adapter (`requestFlowMetrics`) resolves the effective interval and the
 * window/run/lifetime branch internally from `sourceId`, so pass the raw
 * `flowMetricsInterval` and the source id; do not coerce here.
 */
export function useFlowMetricsSync(
  flowNodeId?: string | number | null,
  sourceId?: string | number | null,
  options: UseFlowMetricsSyncOptions = {},
): UseFlowMetricsSyncReturn {
  const { enabled = true } = options;
  const { requestFlowMetrics } = useMetricsAdapter();
  const flowMetricsInterval = useSelector(getFlowMetricsInterval);

  const intervalRef = useRef(flowMetricsInterval);
  intervalRef.current = flowMetricsInterval;

  useEffect(() => {
    if (!enabled || flowNodeId == null) return;
    requestFlowMetrics(flowNodeId, sourceId ?? null, { interval: flowMetricsInterval });
  }, [enabled, flowNodeId, sourceId, flowMetricsInterval, requestFlowMetrics]);

  const refresh = useCallback(() => {
    if (flowNodeId == null) return;
    requestFlowMetrics(flowNodeId, sourceId ?? null, { interval: intervalRef.current });
  }, [flowNodeId, sourceId, requestFlowMetrics]);

  return { refresh };
}

export default useFlowMetricsSync;
